/* eslint-disable no-param-reassign */
import { CellContext } from '@tanstack/react-table';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import Card from '@/components/Card/Card';
import Header from '@/components/Header/Header';
import Icon from '@/components/Icons/Icon';
import StatusTracker from '@/components/StatusTracker/StatusTracker';
import { StatusTrackerStatus } from '@/components/StatusTracker/StatusTracker.types';
import Table from '@/components/Tables/Table/Table';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useFZSubmissionDetails } from '@/services/submissions/submissions.service';
import { Changes, SubmissionSubType, SubmissionSubTypeLabels } from '@/types/submission.types';
import { renderOccasions } from '@/utils/formatting/formatBundleProperties';
import useNavigateWithToast from '@/utils/useNavigateWithToast/useNavigateWithToast';
import styles from './FZSubmissionDetails.module.css';
import { useTranslation } from 'react-i18next';
import Link from '@/components/Link/Link';
import { DateTime } from 'luxon';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

function formatDate(createdDate: string) {
  const date = DateTime.fromISO(createdDate, { zone: 'UTC' }).setZone('local').toLocaleString(DateTime.DATE_SHORT);
  return date;
}
const PricePointCell = ({ row }: CellContext<Changes, unknown>) => {
  const { t } = useTranslation();
  // three scenarios
  // inactive to price (we can't haven't inactive to inactive in the submission details page)
  if (row.original.oldPrice === null || row.original.oldPrice === 99.99) {
    return (
      <div className={styles.cellWithArrow}>
        <span data-testid="inactive_text_when_activating">Inactive</span>
        <Icon iconName="ChevronRight" />
        <span className={styles.bold}>{`$${row.original.newPrice?.toFixed(2)}`}</span>
      </div>
    );
  }
  if (row.original.oldPrice !== null && row.original.newPrice !== null) {
    if (row.original.oldPrice === row.original.newPrice) return <div data-testid="no_price_change" />; // display nada if price is same
    return (
      <div className={styles.cellWithArrow}>
        <span>{`$${row.original.oldPrice?.toFixed(2)}`}</span>
        <Icon iconName="ChevronRight" />
        <span className={styles.bold}>{`$${row.original.newPrice?.toFixed(2)}`}</span>
      </div>
    );
  }
  // price to inactive

  return (
    <div className={styles.cellWithArrow}>
      <span>{`$${row.original.oldPrice?.toFixed(2)}`}</span>
      <Icon iconName="ChevronRight" />
      <span data-testid="inactive text when deactivating" className={styles.bold}>
        {t('common.inactive')}
      </span>
    </div>
  );
};

const TrackerFinish = ({ text1 = '', text2 = '' }) => (
  <div className={styles.statusTrackerFinish}>
    <div>{text1}</div>
    <div>{text2}</div>
  </div>
);

const TrackerFooter = ({ text1 = '', text2 = '', urlText = '', url = '' }) => (
  <div className={styles.statusTrackerFooter}>
    {text1}
    <Link to={url} className={styles.statusTrackerLink}>
      {urlText}
    </Link>
    {text2}
  </div>
);

const RedeemOnlineCell = ({ row }: CellContext<Changes, unknown>) => {
  const { t } = useTranslation();
  // inactive to price
  if (row.original.oldPrice === null) {
    return (
      <div className={styles.cellWithArrow}>
        <span>{t('common.inactive')}</span>
        <Icon iconName="ChevronRight" />
        <span className={styles.bold}>{row.original.newRedeemableOnline ? t('common.yes') : t('common.no')}</span>
      </div>
    );
  }
  // price to price
  if (row.original.oldPrice !== null && row.original.newPrice !== null) {
    if (row.original.oldRedeemableOnline === row.original.newRedeemableOnline)
      return <div data-testid="no_redeem_online_change" />;
    return (
      <div className={styles.cellWithArrow}>
        <span>{row.original.oldRedeemableOnline ? t('common.yes') : t('common.no')}</span>
        <Icon iconName="ChevronRight" />
        <span className={styles.bold}>{row.original.newRedeemableOnline ? t('common.yes') : t('common.no')}</span>
      </div>
    );
  }
  // price to inactive

  return (
    <div className={styles.cellWithArrow}>
      <span>{row.original.oldRedeemableOnline ? t('common.yes') : t('common.no')}</span>
      <Icon iconName="ChevronRight" />
      <span className={styles.bold}>{t('common.inactive')}</span>
    </div>
  );
};

const VisibleOnlineCell = ({ row }: CellContext<Changes, unknown>) => {
  const { t } = useTranslation();
  // inactive to price
  if (row.original.oldPrice === null) {
    return (
      <div className={styles.cellWithArrow}>
        <span>{t('common.inactive')}</span>
        <Icon iconName="ChevronRight" />
        <span className={styles.bold}>{row.original.newVisibleOnline ? t('common.yes') : t('common.no')}</span>
      </div>
    );
  }
  // price to price
  if (row.original.oldPrice !== null && row.original.newPrice !== null) {
    if (row.original.oldVisibleOnline === row.original.newVisibleOnline)
      return <div data-testid="no_visible_online_change" />;
    return (
      <div className={styles.cellWithArrow}>
        <span>{row.original.oldVisibleOnline ? t('common.yes') : t('common.no')}</span>
        <Icon iconName="ChevronRight" />
        <span className={styles.bold}>{row.original.newVisibleOnline ? t('common.yes') : t('common.no')}</span>
      </div>
    );
  }
  // price to inactive

  return (
    <div className={styles.cellWithArrow}>
      <span>{row.original.oldVisibleOnline ? t('common.yes') : t('common.no')}</span>
      <Icon iconName="ChevronRight" />
      <span className={styles.bold}>{t('common.inactive')}</span>
    </div>
  );
};

const FZSubmissionDetails: FC = () => {
  const { t } = useTranslation();
  const { orgId: id } = useParams();
  const navigateWithToast = useNavigateWithToast();
  const { data, isLoading: submissionDetailsLoading, isError } = useFZSubmissionDetails(id !== undefined, id!);

  if (isError) {
    navigateWithToast('/', {
      label: t('error.fetchingSubmissionDetail'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingSubmissionDetail',
    });
  }

  const displayName = data?.data?.name || '-';
  const internalName = data?.data?.internalName || '-';
  const recommendedPrices: number[] = data?.data?.recommendedPrices || [];
  const formattedPrices = recommendedPrices
    ?.map((price) => {
      if (!price || price === undefined || price === null) return '';
      return price && `$${price?.toFixed(2)}`;
    })
    .filter((priceValue) => priceValue)
    .join(', ');
  const occasion = renderOccasions(t, data?.data?.occasions);

  const isMissingPricePoints = !submissionDetailsLoading && recommendedPrices?.length === 0;

  const humanReadable = SubmissionSubTypeLabels[data?.data?.subType as SubmissionSubType];
  return (
    <div>
      <Header
        title={t('submissions.details.header')}
        breadcrumb={{ label: t('breadcrumb.backToYourSubmissions'), to: '/submissions' }}
      />
      <div className={styles.page}>
        <div className={styles.profileSection}>
          <div className={styles.infoRow}>
            <h2>{t('submissions.details.submittedLabel')}:</h2>

            {data?.data?.createdDate && formatDate(data?.data?.createdDate)}
          </div>
          <div className={styles.infoRow}>
            <h2>{t('submissions.details.typeLabel')}:</h2>
            {humanReadable}
          </div>
        </div>
        <StatusTracker
          submitted={data?.data?.createdDate && formatDate(data.data.createdDate)}
          progress={data?.data?.status === 'PENDING' ? 50 : 100}
          status={data?.data?.status === 'PENDING' ? StatusTrackerStatus.Pending : StatusTrackerStatus.Completed}
          finished={data?.data?.completedDate ? formatDate(data.data.completedDate) : t('common.hours', { count: 48 })}
          finishNote={
            data?.data?.status === 'PENDING' ? undefined : (
              <TrackerFinish
                text1={t('submissions.statusTracker.finishLabel1')}
                text2={t('submissions.statusTracker.finishLabel2')}
              />
            )
          }
          footerNote={null}
          banner={
            data?.data?.status === 'PENDING' ? (
              <CalendarAlert warningIcon requestedVerbiage={false} addCalendarLink />
            ) : undefined
          }
        />
        {data?.data?.type === 'BUNDLE' ? (
          <Card className={styles.card}>
            <div data-testid="bundleCard" className={styles.row}>
              <div className={styles.column}>
                <p>{t('submissions.table.name')}: </p>
                <p>{t('submissions.table.displayName')}: </p>
                <p>{t('submissions.table.price')}: </p>
                <p>{t('submissions.table.occasion')}: </p>
              </div>
              {data?.data && (
                <div className={styles.columnDescriptions}>
                  <p>{internalName}</p>
                  <p>{displayName}</p>
                  {!isMissingPricePoints ? (
                    formattedPrices
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      <Icon iconName="Info" className={styles.iconButton} style={{ paddingLeft: 0 }} />
                      <p style={{ color: 'var(--red-700)' }}> {t('common.bundle.missingPricePointsLabel')} </p>
                    </div>
                  )}
                  <p>{occasion}</p>
                </div>
              )}
            </div>
          </Card>
        ) : (
          <Card className={styles.card}>
            <div data-testid="groupCard" className={styles.header}>
              <div className={styles.heading}>{t('submissions.details.storeGroupDetails')}</div>
            </div>
            <div className={styles.withBorder} />
            <div className={styles.programDetails}>
              <div className={styles.groupColumn}>
                <div className={styles.groupRow}>
                  <div>{t('submissions.details.storeGroupName')}:</div>
                  <div>{data?.data?.newGroupName}</div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {data?.data?.type === 'BUNDLE' ? (
          <Card className={styles.card}>
            <div data-testid="bundleTableCard" className={styles.header}>
              <div className={styles.heading}>{t('common.changes')}</div>
            </div>
            <div className={styles.withBorder} />
            <div className={styles.submissionTable}>
              <Table
                width="95%"
                selectedRows={undefined}
                data={data?.data.changes || []}
                headersToWrap={['visibleOnline', 'redeemOnline']}
                columns={[
                  { id: 'groups', accessorKey: 'groupName', header: t('submissions.table.affectedGroups') },
                  {
                    id: 'pricePoint',
                    header: t('submissions.table.pricePoint'),
                    cell: PricePointCell,
                  },
                  {
                    id: 'redeemOnline',
                    header: t('submissions.table.redeemOnline'),
                    cell: RedeemOnlineCell,
                  },
                  {
                    id: 'visibleOnline',
                    header: t('submissions.table.visibleOnline'),
                    cell: VisibleOnlineCell,
                  },
                ]}
              />
            </div>
          </Card>
        ) : (
          <Card className={styles.card}>
            <div data-testid="groupTableCard" className={styles.header}>
              <div className={styles.heading}>{t('common.changes')}</div>
            </div>
            <div className={styles.withBorder} />
            <div className={styles.submissionTable}>
              <Table
                width="95%"
                selectedRows={undefined}
                enableRowSelection={false}
                isLoading={false}
                loadingRowCount={7}
                data={data?.data.changes || []}
                columns={[
                  {
                    accessorKey: 'storeNo',
                    header: t('submissions.table.affectedStores'),
                    cell: (props) => (!props.row.original.storeNo ? '-' : `${props.row.original.storeNo}`),
                  },
                  {
                    accessorKey: 'oldBundleGroup',
                    header: t('submissions.table.originalBundleGroup'),
                    cell: (props) => (!props.row.original.oldGroupName ? '-' : `${props.row.original.oldGroupName}`),
                  },
                ]}
                onRowSelectionChange={undefined}
              />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FZSubmissionDetails;
